import * as RS from '@radix-ui/react-select';
import { useId, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from './cn';
import { FieldShell, inputBase } from './Field';

export interface SelectOption {
  value: string;
  label: ReactNode;
  /** One line under the option in the open list (never in the closed trigger). */
  description?: ReactNode;
  /** Shown but not choosable; say why in `description`. */
  disabled?: boolean;
  /** A heading the option is listed under; consecutive options sharing one form a group. */
  group?: string;
  /** What `SearchableSelect` matches against when `label` is not a plain string. */
  textValue?: string;
}

/** Consecutive options under the same heading, in order. */
function grouped(options: readonly SelectOption[]): { group?: string; options: SelectOption[] }[] {
  const out: { group?: string; options: SelectOption[] }[] = [];
  for (const opt of options) {
    const last = out[out.length - 1];
    if (last && last.group === opt.group) last.options.push(opt);
    else out.push({ group: opt.group, options: [opt] });
  }
  return out;
}

// Radix's Select.Item forbids an empty-string value (it's reserved for clearing
// the selection). Map empty strings to/from an internal sentinel so call sites
// can keep using '' to mean "no filter / unset".
const EMPTY_SENTINEL = '__empty__';
const toInternal = (v: string) => (v === '' ? EMPTY_SENTINEL : v);
const fromInternal = (v: string) => (v === EMPTY_SENTINEL ? '' : v);

export function Select({
  label,
  value,
  onValueChange,
  options,
  placeholder,
  hint,
  required,
  error,
  disabled,
  className,
}: {
  label?: ReactNode;
  value: string | undefined;
  onValueChange: (v: string) => void;
  options: SelectOption[];
  placeholder?: string;
  /**
   * Editor-facing note rendered under the control, exactly as on a text input.
   * A closed choice is precisely where the note matters most: a dropdown shows
   * WHICH values are allowed and never WHY the distinction exists — and some of
   * these carry a rule (a metric's achieved-vs-goal, a match's target year) that
   * an editor cannot infer from four words in a list.
   */
  hint?: ReactNode;
  required?: boolean;
  error?: string | null;
  disabled?: boolean;
  className?: string;
}) {
  const { t } = useTranslation();
  const id = useId();
  return (
    <FieldShell label={label} htmlFor={id} required={required} error={error} hint={hint}>
      <RS.Root
        value={value === undefined ? undefined : toInternal(value)}
        onValueChange={(v) => onValueChange(fromInternal(v))}
        disabled={disabled}
      >
        <RS.Trigger
          id={id}
          aria-required={required || undefined}
          aria-invalid={error ? true : undefined}
          className={cn(
            inputBase,
            'flex items-center justify-between gap-sm text-start',
            error && 'border-signal-error',
            className,
          )}
        >
          <RS.Value placeholder={placeholder ?? t('ui.selectPlaceholder')} />
          <RS.Icon aria-hidden className="text-ink-mute">
            ▾
          </RS.Icon>
        </RS.Trigger>
        <RS.Portal>
          <RS.Content
            position="popper"
            sideOffset={4}
            className="z-95 max-h-(--radix-select-content-available-height) min-w-(--radix-select-trigger-width) overflow-auto rounded-sm border-[0.5px] border-hairline bg-canvas-paper py-2xs shadow-nav-scrolled"
          >
            <RS.Viewport>
              {grouped(options).map((g, gi) => {
                const items = g.options.map((opt) => (
                  <RS.Item
                    key={opt.value}
                    value={toInternal(opt.value)}
                    disabled={opt.disabled}
                    className="flex cursor-pointer flex-col items-start px-sm py-xs text-small text-ink-graphite outline-hidden data-highlighted:bg-canvas-sand data-[state=checked]:text-accent-blue data-disabled:cursor-not-allowed data-disabled:text-ink-mute"
                  >
                    <RS.ItemText>{opt.label}</RS.ItemText>
                    {opt.description && (
                      <span className="max-w-prose text-small text-ink-mute">{opt.description}</span>
                    )}
                  </RS.Item>
                ));
                return g.group ? (
                  <RS.Group key={`${gi}-${g.group}`}>
                    <RS.Label className="px-sm pt-xs pb-2xs text-small font-medium text-ink-mute">{g.group}</RS.Label>
                    {items}
                  </RS.Group>
                ) : (
                  items
                );
              })}
            </RS.Viewport>
          </RS.Content>
        </RS.Portal>
      </RS.Root>
    </FieldShell>
  );
}
